import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { KeyRound, Loader2, Lock, ShieldAlert } from 'lucide-react';
import { apiPost } from '../shared/apiClient';
import { useToast } from '../hooks/useToast';

interface MustChangePasswordModalProps {
  isOpen: boolean;
  onSuccess: () => void;
}

export function MustChangePasswordModal({ isOpen, onSuccess }: MustChangePasswordModalProps) {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  if (!isOpen) return null;
  if (typeof document === 'undefined') return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!oldPassword) {
      toast({ type: 'error', message: 'Inserisci la tua password attuale' });
      return;
    }
    if (newPassword.length < 6) {
      toast({ type: 'error', message: 'La nuova password deve contenere almeno 6 caratteri' });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ type: 'error', message: 'La nuova password e la conferma non coincidono' });
      return;
    }

    setLoading(true);
    try {
      await apiPost('/auth/change-password', { oldPassword, newPassword });
      toast({ type: 'success', message: 'Password aggiornata con successo!' });
      onSuccess();
    } catch (err: any) {
      const msg = err?.response?.data?.errore || err?.message || 'Errore durante il cambio password';
      toast({ type: 'error', message: msg });
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-md animate-fade-in">
      <div className="w-full max-w-md bg-white rounded-3xl p-6 shadow-2xl space-y-4 border border-amber-200">
        <div className="flex items-center gap-3 border-b border-slate-100 pb-3">
          <div className="w-10 h-10 rounded-2xl bg-amber-100 text-amber-700 flex items-center justify-center font-bold">
            <ShieldAlert className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-extrabold text-slate-900 text-base sm:text-lg">
              Cambio Password Obbligatorio
            </h3>
            <p className="text-xs text-slate-500">
              È richiesto impostare una nuova password personale per proseguire.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3.5 text-xs sm:text-sm">
          <div className="space-y-1">
            <label className="block font-bold text-slate-700">Password Attuale</label>
            <div className="relative">
              <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
              <input
                type="password"
                className="input pl-9 text-xs font-semibold"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                placeholder="Inserisci password attuale..."
                required
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="block font-bold text-slate-700">Nuova Password (min. 6 caratteri)</label>
            <div className="relative">
              <KeyRound className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
              <input
                type="password"
                className="input pl-9 text-xs font-semibold"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Nuova password..."
                required
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="block font-bold text-slate-700">Conferma Nuova Password</label>
            <div className="relative">
              <KeyRound className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
              <input
                type="password"
                className="input pl-9 text-xs font-semibold"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Conferma nuova password..."
                required
              />
            </div>
          </div>

          <div className="pt-2">
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary w-full justify-center text-sm font-extrabold py-3 gap-2 shadow-md"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              <span>Aggiorna Password e Continua</span>
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
